import React, { useRef, useEffect, useState, useCallback } from 'react';

const ScratchCard = ({
  width,
  height,
  children,
  onComplete,
  brushSize = 35,
  finishPercent = 50,
  image = null,
  color = '#888'
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const scratchCount = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    // Fill overlay
    if (image) {
      const img = new Image();
      img.src = image;
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
      };
    } else {
      // Create a nice gradient
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#94a3b8');
      gradient.addColorStop(0.5, '#cbd5e1');
      gradient.addColorStop(1, '#64748b');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Add some noise or pattern
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      for (let i = 0; i < 60; i++) {
        ctx.beginPath();
        ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 5 + 1, 0, 2 * Math.PI);
        ctx.fill();
      }

      // Add "Scratch Here" text
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.font = 'bold 26px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('SCRATCH HERE', width / 2, height / 2);
    }
  }, [width, height, image, color]);

  const getPointerPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    
    // Normalize touch and mouse events safely
    let clientX = e.clientX;
    let clientY = e.clientY;
    
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const checkCompletion = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || isFinished) return;
    const ctx = canvas.getContext('2d');
    
    try {
      const imageData = ctx.getImageData(0, 0, width, height);
      const pixels = imageData.data;
      let transparentPixels = 0;
      const totalPixels = pixels.length / 4;

      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i + 3] < 128) { // Less strict alpha check to account for anti-aliasing
          transparentPixels++;
        }
      }

      const percentage = (transparentPixels / totalPixels) * 100;
      if (percentage >= finishPercent) {
        setIsFinished(true);
        // Clear the rest
        ctx.clearRect(0, 0, width, height);
        if (onComplete) {
          onComplete();
        }
      }
    } catch (e) {
      console.error("Canvas read error:", e);
    }
  }, [width, height, finishPercent, isFinished, onComplete]);

  const scratch = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas || isFinished) return;
    const ctx = canvas.getContext('2d');
    const pos = getPointerPos(e);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize, 0, 2 * Math.PI, false);
    ctx.fill();

    // Check completion periodically while scratching (e.g. every 10th frame) to feel more responsive
    scratchCount.current += 1;
    if (scratchCount.current % 10 === 0) {
      checkCompletion();
    }
  }, [brushSize, isFinished, checkCompletion]);

  // Native event listeners to safely prevent default on touch without React synthetic event warnings
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onTouchStart = (e) => {
      e.preventDefault(); // Prevents pull-to-refresh on Telegram
      if (isFinished) return;
      setIsDrawing(true);
      scratch(e);
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (!isDrawing || isFinished) return;
      scratch(e);
    };

    const onTouchEnd = (e) => {
      e.preventDefault();
      if (isFinished) return;
      setIsDrawing(false);
      checkCompletion();
    };

    const onMouseDown = (e) => {
      if (isFinished) return;
      setIsDrawing(true);
      scratch(e);
    };

    const onMouseMove = (e) => {
      if (!isDrawing || isFinished) return;
      scratch(e);
    };

    const onMouseUp = () => {
      if (isFinished) return;
      setIsDrawing(false);
      checkCompletion();
    };

    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });
    
    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDrawing, isFinished, scratch, checkCompletion]);

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: 'relative', 
        width: `${width}px`, 
        height: `${height}px`,
        overflow: 'hidden',
        borderRadius: '16px',
        touchAction: 'none' // Fallback for browsers
      }}
      className="shadow-inner"
    >
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        {children}
      </div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          cursor: isFinished ? 'default' : 'crosshair', 
          transition: 'opacity 0.5s', 
          opacity: isFinished ? 0 : 1 
        }}
      />
    </div>
  );
};

export default ScratchCard;
